import React from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import AuthGate from '@components/auth/AuthGate';
import LoginView from '@components/auth/LoginView';
import { LOGIN_ENABLED, MOCK_FALLBACK_ENABLED } from '@constants/auth';
import {
  PAGE_TAB_VALUES,
  normalizePageTabValue,
} from '@constants/pageTabs';
import { ROUTES } from '@constants/routes';
import { useAuth } from '@hooks/useAuth';
import { useEnsureIframeParams } from '@hooks/useEnsureIframeParams';
import { useEnsureIndicesParams } from '@hooks/useEnsureIndicesParams';
import { useWidgetHeightPostMessage } from '@hooks/useWidgetHeightPostMessage';
import { useWidgetPageSettled } from '@hooks/useWidgetPageSettled';
import {
  appendPageTabParam,
  appendPostLoginIframeParams,
  getIframeQueryParamsFromSearch,
} from '@utils/queryParams';
import { getLastInstrumentContext } from '@utils/iframeContextStorage';
import type { TokenRequest } from '@app-types/auth';
import PerformanceSummaryPage from '@pages/PerformanceSummary/PerformanceSummaryPage';
import PerformanceTrendsPage from '@pages/PerformanceTrends/PerformanceTrendsPage';
import PerformanceDividendPage from '@pages/PerformanceDividend/PerformanceDividendPage';
import ComparisonPage from '@pages/Comparison/ComparisonPage';
import InstructionsPage from '@pages/Instructions/InstructionsPage';
import InvestmentIndicatorsPage from '@pages/InvestmentIndicators/InvestmentIndicatorsPage';
import IndicesPage from '@pages/Indices/IndicesPage';
import { internalRoutes } from '@internal-routes';

/**
 * Redirects to /analysis summary while preserving iframe/query context.
 */
const RedirectToAnalysisSummary: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: ROUTES.analysis,
        search: appendPageTabParam(location.search, PAGE_TAB_VALUES.summary),
      }}
      replace
    />
  );
};

interface LoginLocationState {
  from?: {
    pathname?: string;
    search?: string;
  };
}

const useLoginRedirectTarget = () => {
  const location = useLocation();
  const state = location.state as LoginLocationState | null;
  const from = state?.from;
  const pathname = from?.pathname && from.pathname !== ROUTES.login
    ? from.pathname
    : ROUTES.analysis;
  const search = from?.search ?? location.search;

  return { pathname, search };
};

const LoginRoute: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const redirectTarget = useLoginRedirectTarget();

  if (auth.status === 'initializing') {
    return <div className="auth-gate-status" role="status">認証情報を確認しています…</div>;
  }
  if (!LOGIN_ENABLED && MOCK_FALLBACK_ENABLED) {
    return <Navigate to={redirectTarget} replace />;
  }
  if (!LOGIN_ENABLED) {
    return (
      <div className="auth-gate-status" role="alert">
        認証が必要ですが、ログイン機能が無効になっています。
      </div>
    );
  }

  const handleSubmit = async (request: TokenRequest, remember: boolean) => {
    const token = await auth.login(request, remember);
    const search = appendPostLoginIframeParams(
      redirectTarget.search,
      token,
      getLastInstrumentContext(),
      PAGE_TAB_VALUES.summary,
    );

    navigate(
      { ...redirectTarget, search },
      { replace: true },
    );
  };

  return <LoginView onSubmit={handleSubmit} />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthGate>{children}</AuthGate>
);

const AnalysisPageByTab: React.FC = () => {
  const location = useLocation();
  const tab = normalizePageTabValue(
    getIframeQueryParamsFromSearch(location.search).tab,
  ) ?? PAGE_TAB_VALUES.summary;

  switch (tab) {
    case PAGE_TAB_VALUES.performanceTrends:
      return <PerformanceTrendsPage />;
    case PAGE_TAB_VALUES.dividends:
      return <PerformanceDividendPage />;
    case PAGE_TAB_VALUES.comparison:
      return <ComparisonPage />;
    case PAGE_TAB_VALUES.instructions:
      return <InstructionsPage />;
    case PAGE_TAB_VALUES.investmentIndicators:
      return <InvestmentIndicatorsPage />;
    case PAGE_TAB_VALUES.summary:
    default:
      return <PerformanceSummaryPage />;
  }
};

const AnalysisHeightReporter: React.FC = () => {
  const isPageSettled = useWidgetPageSettled();
  useWidgetHeightPostMessage({
    enabled: true,
    isPageSettled,
  });

  return null;
};

const AnalysisPageWithHeightMessaging: React.FC = () => (
  <>
    <AnalysisPageByTab />
    <AnalysisHeightReporter />
  </>
);

const AnalysisRoute: React.FC = () => {
  const hasIframeParams = useEnsureIframeParams();

  if (!hasIframeParams) return null;

  return (
    <ProtectedRoute>
      <AnalysisPageWithHeightMessaging />
    </ProtectedRoute>
  );
};

const IndicesRoute: React.FC = () => {
  const hasIndicesParams = useEnsureIndicesParams();

  if (!hasIndicesParams) return null;

  return <ProtectedRoute><IndicesPage /></ProtectedRoute>;
};

const AppRoutes: React.FC = () => (
  <Routes>
    {internalRoutes}
    <Route path={ROUTES.login} element={<LoginRoute />} />
    <Route path={ROUTES.analysis} element={<AnalysisRoute />} />
    <Route path={ROUTES.indices} element={<IndicesRoute />} />
    <Route path="/" element={<RedirectToAnalysisSummary />} />
    <Route path="*" element={<RedirectToAnalysisSummary />} />
  </Routes>
);

const App: React.FC = () => <AppRoutes />;

export default App;
